import React, { useState, useEffect, useMemo } from 'react';
import { Link } from 'react-router-dom';
import { api, API } from '../App';
import { useAuth } from '../App';
import { toast } from 'sonner';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Badge } from '../components/ui/badge';
import { Textarea } from '../components/ui/textarea';
import { Switch } from '../components/ui/switch';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from '../components/ui/dialog';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '../components/ui/table';
import { Plus, Trash2, Package, Edit, FileText, Filter, Download, Users, ExternalLink, Link2 } from 'lucide-react';
import { DatePickerField } from '../components/DatePickerField';
import { format } from 'date-fns';

const STANDARD_BYPRODUCTS = ['Bran', 'Germ', 'Unga Baridi', 'Others'];

export default function Production() {
  const { isAdmin } = useAuth();
  const [productions, setProductions] = useState([]);
  const [teams, setTeams] = useState([]);
  const [packSizes, setPackSizes] = useState([]);
  const [maizeBatches, setMaizeBatches] = useState([]);  // Phase 1
  const [loading, setLoading] = useState(true);

  // Production form
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [date, setDate] = useState(new Date());
  const [formData, setFormData] = useState({
    factory: '',
    pack_size: '',
    actual_weight_per_packet: '',
    num_bales: '',
    packets_per_bale: '',
    packaging_team_id: '',
    packaging_cost_per_bale: '',
    remarks: '',
    details: '',
    purchase_batch_id: ''  // Phase 1
  });
  const [byproducts, setByproducts] = useState([]);

  // Filters
  const [filterStart, setFilterStart] = useState(null);
  const [filterEnd, setFilterEnd] = useState(null);
  const [filterFactory, setFilterFactory] = useState('all');
  const [filterPackSize, setFilterPackSize] = useState('all');
  const [filterTeamId, setFilterTeamId] = useState('all');

  // Teams dialog
  const [teamsDialogOpen, setTeamsDialogOpen] = useState(false);
  const [editingTeamId, setEditingTeamId] = useState(null);
  const [teamForm, setTeamForm] = useState({ name: '', leader: '', phone: '', notes: '', is_active: true });

  const [downloading, setDownloading] = useState(false);

  useEffect(() => {
    fetchAll();
  }, []);

  const fetchAll = async () => {
    try {
      const [pRes, tRes, psRes, mbRes] = await Promise.all([
        api.get('/productions'),
        api.get('/packaging-teams?include_inactive=true'),
        api.get('/pack-sizes').catch(() => ({ data: [] })),
        api.get('/purchases/maize-batches').catch(() => ({ data: [] })),
      ]);
      setProductions(pRes.data);
      setTeams(tRes.data);
      setPackSizes(psRes.data || []);
      setMaizeBatches(mbRes.data || []);
    } catch (e) {
      toast.error('Failed to load data');
    } finally {
      setLoading(false);
    }
  };

  // Build unique pack-size options: from configured + already used
  const packSizeOptions = useMemo(() => {
    const fromConfig = (packSizes || []).map(p => `${p.size_kg}kg`);
    const fromRecords = productions.map(p => p.pack_size).filter(Boolean);
    return Array.from(new Set([...fromConfig, ...fromRecords]));
  }, [packSizes, productions]);

  // Filtered productions
  const filtered = useMemo(() => {
    return productions.filter(p => {
      if (filterStart && p.date < format(filterStart, 'yyyy-MM-dd')) return false;
      if (filterEnd && p.date > format(filterEnd, 'yyyy-MM-dd')) return false;
      if (filterFactory !== 'all' && p.factory !== filterFactory) return false;
      if (filterPackSize !== 'all' && p.pack_size !== filterPackSize) return false;
      if (filterTeamId !== 'all' && p.packaging_team_id !== filterTeamId) return false;
      return true;
    });
  }, [productions, filterStart, filterEnd, filterFactory, filterPackSize, filterTeamId]);

  const summary = useMemo(() => ({
    records: filtered.length,
    bales: filtered.reduce((s, p) => s + (p.num_bales || 0), 0),
    packets: filtered.reduce((s, p) => s + (p.total_packets || 0), 0),
    weight: filtered.reduce((s, p) => s + (p.total_weight_packed || 0), 0),
    earned: filtered.reduce((s, p) => s + (p.total_amount_earned || 0), 0),
  }), [filtered]);

  // Auto-suggest packaging cost from selected pack-size config (one-time on pack_size change)
  useEffect(() => {
    if (!formData.pack_size || editingId) return;
    const match = packSizes.find(p => `${p.size_kg}kg` === formData.pack_size);
    if (match && !formData.packaging_cost_per_bale) {
      setFormData(fd => ({ ...fd, packaging_cost_per_bale: (match.packaging_cost_per_bale || '').toString() }));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [formData.pack_size]);

  // Live computed previews in the form
  const formCalc = useMemo(() => {
    const bales = parseFloat(formData.num_bales) || 0;
    const ppb = parseFloat(formData.packets_per_bale) || 0;
    const wpp = parseFloat(formData.actual_weight_per_packet) || 0;
    const cpb = parseFloat(formData.packaging_cost_per_bale) || 0;
    const total_packets = bales * ppb;
    const total_weight_packed = total_packets * wpp;
    const total_amount_earned = bales * cpb;
    return { total_packets, total_weight_packed, total_amount_earned };
  }, [formData]);

  const addByproduct = () => setByproducts([...byproducts, { id: Date.now().toString(), name: '', quantity_kg: 0, notes: '', unit_price_per_kg: '', total_value: '' }]);
  const updateByproduct = (id, field, value) => setByproducts(byproducts.map(bp => {
    if (bp.id !== id) return bp;
    let updated = { ...bp };
    if (field === 'name' || field === 'notes') {
      updated[field] = value;
    } else if (field === 'quantity_kg' || field === 'unit_price_per_kg') {
      updated[field] = value === '' ? '' : (parseFloat(value) || 0);
      // Auto-calc total_value if user hasn't manually overridden it
      const qty = parseFloat(field === 'quantity_kg' ? value : updated.quantity_kg) || 0;
      const price = parseFloat(field === 'unit_price_per_kg' ? value : updated.unit_price_per_kg) || 0;
      if (qty > 0 && price > 0 && !updated._manual_total) {
        updated.total_value = Math.round(qty * price * 100) / 100;
      }
    } else if (field === 'total_value') {
      updated.total_value = value === '' ? '' : (parseFloat(value) || 0);
      updated._manual_total = true;
    }
    return updated;
  }));
  const removeByproduct = (id) => setByproducts(byproducts.filter(bp => bp.id !== id));

  const resetForm = () => {
    setFormData({ factory: '', pack_size: '', actual_weight_per_packet: '', num_bales: '', packets_per_bale: '', packaging_team_id: '', packaging_cost_per_bale: '', remarks: '', details: '', purchase_batch_id: '' });
    setByproducts([]);
    setDate(new Date());
    setEditingId(null);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!formData.factory) { toast.error('Please select a factory'); return; }

    const validByproducts = byproducts.filter(bp => bp.name && bp.quantity_kg > 0);
    for (const bp of validByproducts) {
      if (bp.name === 'Others' && !bp.notes?.trim()) {
        toast.error('Description is required for "Others" byproduct'); return;
      }
    }

    const team = teams.find(t => t.id === formData.packaging_team_id);
    const batch = maizeBatches.find(b => b.id === formData.purchase_batch_id);
    const payload = {
      date: format(date, 'yyyy-MM-dd'),
      factory: formData.factory,
      details: formData.details || '',
      remarks: formData.remarks || null,
      pack_size: formData.pack_size || null,
      actual_weight_per_packet: formData.actual_weight_per_packet ? parseFloat(formData.actual_weight_per_packet) : null,
      num_bales: formData.num_bales ? parseFloat(formData.num_bales) : null,
      packets_per_bale: formData.packets_per_bale ? parseFloat(formData.packets_per_bale) : null,
      packaging_cost_per_bale: formData.packaging_cost_per_bale ? parseFloat(formData.packaging_cost_per_bale) : null,
      packaging_team_id: formData.packaging_team_id || null,
      packaging_team_name: team ? team.name : null,
      purchase_batch_id: formData.purchase_batch_id || null,
      purchase_batch_no: batch ? batch.batch_no : null,
      byproducts: validByproducts.map(bp => ({
        name: bp.name,
        quantity_kg: bp.quantity_kg,
        notes: bp.notes || null,
        unit_price_per_kg: bp.unit_price_per_kg != null && bp.unit_price_per_kg !== '' ? parseFloat(bp.unit_price_per_kg) : null,
        total_value: bp.total_value != null && bp.total_value !== '' ? parseFloat(bp.total_value) : null,
      }))
    };

    try {
      if (editingId) {
        await api.put(`/productions/${editingId}`, payload);
        toast.success('Production updated');
      } else {
        await api.post('/productions', payload);
        toast.success('Production recorded');
      }
      setDialogOpen(false);
      resetForm();
      fetchAll();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to save');
    }
  };

  const handleEdit = (prod) => {
    setEditingId(prod.id);
    setDate(new Date(prod.date));
    setFormData({
      factory: prod.factory || '',
      pack_size: prod.pack_size || '',
      actual_weight_per_packet: prod.actual_weight_per_packet?.toString() || '',
      num_bales: prod.num_bales?.toString() || '',
      packets_per_bale: prod.packets_per_bale?.toString() || '',
      packaging_team_id: prod.packaging_team_id || '',
      packaging_cost_per_bale: prod.packaging_cost_per_bale?.toString() || '',
      remarks: prod.remarks || '',
      details: prod.details || '',
      purchase_batch_id: prod.purchase_batch_id || ''
    });
    setByproducts(prod.byproducts?.map((bp, idx) => ({
      id: `${Date.now()}-${idx}`,
      name: bp.name,
      quantity_kg: bp.quantity_kg,
      notes: bp.notes || '',
      unit_price_per_kg: bp.unit_price_per_kg ?? '',
      total_value: bp.total_value ?? '',
    })) || []);
    setDialogOpen(true);
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Delete this production record?')) return;
    try {
      await api.delete(`/productions/${id}`);
      toast.success('Production deleted');
      fetchAll();
    } catch (e) {
      toast.error('Failed to delete');
    }
  };

  // Teams handlers
  const resetTeamForm = () => { setTeamForm({ name: '', leader: '', phone: '', notes: '', is_active: true }); setEditingTeamId(null); };

  const handleTeamSubmit = async (e) => {
    e.preventDefault();
    if (!teamForm.name.trim()) { toast.error('Team name is required'); return; }
    try {
      if (editingTeamId) {
        await api.put(`/packaging-teams/${editingTeamId}`, teamForm);
        toast.success('Team updated');
      } else {
        await api.post('/packaging-teams', teamForm);
        toast.success('Team created');
      }
      resetTeamForm();
      fetchAll();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed');
    }
  };

  const editTeam = (team) => {
    setEditingTeamId(team.id);
    setTeamForm({
      name: team.name,
      leader: team.leader || '',
      phone: team.phone || '',
      notes: team.notes || '',
      is_active: team.is_active !== false
    });
  };

  const deleteTeam = async (id) => {
    if (!window.confirm('Delete this packaging team?')) return;
    try {
      await api.delete(`/packaging-teams/${id}`);
      toast.success('Team deleted');
      fetchAll();
    } catch {
      toast.error('Failed to delete');
    }
  };

  const clearFilters = () => {
    setFilterStart(null); setFilterEnd(null);
    setFilterFactory('all'); setFilterPackSize('all'); setFilterTeamId('all');
  };

  const fmt = (n) => new Intl.NumberFormat('en-KE').format(Number(n || 0).toFixed(2));
  const fmtKES = (n) => new Intl.NumberFormat('en-KE', { style: 'currency', currency: 'KES', minimumFractionDigits: 0 }).format(n || 0);

  const downloadPDF = async () => {
    setDownloading(true);
    try {
      const params = new URLSearchParams();
      if (filterStart) params.append('start_date', format(filterStart, 'yyyy-MM-dd'));
      if (filterEnd) params.append('end_date', format(filterEnd, 'yyyy-MM-dd'));
      if (filterFactory !== 'all') params.append('factory', filterFactory);
      if (filterPackSize !== 'all') params.append('pack_size', filterPackSize);
      if (filterTeamId !== 'all') params.append('packaging_team_id', filterTeamId);

      const url = `${API}/productions/report/pdf${params.toString() ? '?' + params.toString() : ''}`;
      const token = localStorage.getItem('token');
      const res = await fetch(url, { headers: { Authorization: `Bearer ${token}` } });
      if (!res.ok) throw new Error('Failed');
      const blob = await res.blob();
      const dl = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = dl;
      a.download = `Production_Records_${format(new Date(), 'yyyyMMdd')}.pdf`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      window.URL.revokeObjectURL(dl);
      toast.success('PDF downloaded');
    } catch {
      toast.error('Failed to download PDF');
    } finally {
      setDownloading(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-[#1B4D3E]"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="production-page">
      {/* Header */}
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div>
          <h1 className="text-2xl sm:text-3xl font-heading font-bold text-[#1B4D3E]">Production</h1>
          <p className="text-slate-500 mt-1">Operational production records, packaging teams & statements</p>
        </div>
        <div className="flex flex-wrap gap-2">
          <Button variant="outline" className="gap-2" onClick={() => setTeamsDialogOpen(true)} data-testid="manage-teams-btn">
            <Users className="h-4 w-4" /> Packaging Teams
          </Button>
          <Button variant="outline" className="gap-2" onClick={downloadPDF} disabled={downloading} data-testid="download-production-pdf">
            <Download className="h-4 w-4" /> {downloading ? 'Downloading…' : 'Download PDF'}
          </Button>
          <Dialog open={dialogOpen} onOpenChange={(open) => { setDialogOpen(open); if (!open) resetForm(); }}>
            <DialogTrigger asChild>
              <Button className="btn-primary gap-2" data-testid="add-production-btn">
                <Plus className="h-4 w-4" /> Add Production
              </Button>
            </DialogTrigger>
            <DialogContent className="sm:max-w-3xl max-h-[90vh] overflow-y-auto">
              <DialogHeader>
                <DialogTitle className="font-heading">{editingId ? 'Edit Production' : 'Record Production'}</DialogTitle>
              </DialogHeader>
              <form onSubmit={handleSubmit} className="space-y-4">
                <div className="grid grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label>Date</Label>
                    <DatePickerField date={date} onDateChange={setDate} />
                  </div>
                  <div className="space-y-2">
                    <Label>Factory *</Label>
                    <Select value={formData.factory} onValueChange={(v) => setFormData({ ...formData, factory: v })}>
                      <SelectTrigger data-testid="production-factory-select"><SelectValue placeholder="Select factory" /></SelectTrigger>
                      <SelectContent>
                        <SelectItem value="Ndumberi">Ndumberi</SelectItem>
                        <SelectItem value="Githunguri">Githunguri</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                </div>

                {/* Phase 1: Purchase Batch link */}
                <div className="space-y-2">
                  <Label className="flex items-center gap-1"><Link2 className="h-4 w-4 text-emerald-600" />Purchase Batch <span className="text-xs text-slate-400 font-normal">(source maize)</span></Label>
                  <Select
                    value={formData.purchase_batch_id}
                    onValueChange={(v) => {
                      const b = maizeBatches.find(x => x.id === v);
                      setFormData(fd => ({
                        ...fd,
                        purchase_batch_id: v,
                        factory: fd.factory || (b?.factory || '')
                      }));
                    }}
                  >
                    <SelectTrigger data-testid="production-batch-select">
                      <SelectValue placeholder="Optional — link to a maize purchase batch" />
                    </SelectTrigger>
                    <SelectContent className="max-h-80">
                      {maizeBatches.length === 0 && <SelectItem value="__none__" disabled>No batches available</SelectItem>}
                      {maizeBatches.map(b => (
                        <SelectItem key={b.id} value={b.id}>
                          {b.batch_no} — {b.date} — {(b.kg_received || 0).toLocaleString()} kg — {b.supplier_name || '-'}
                          {b.linked_production_count > 0 ? `  (${b.linked_production_count} linked)` : ''}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                  {formData.purchase_batch_id && (() => {
                    const b = maizeBatches.find(x => x.id === formData.purchase_batch_id);
                    if (!b) return null;
                    return (
                      <div className="text-[11px] text-slate-500 flex flex-wrap gap-x-4 gap-y-0.5 bg-emerald-50/50 border border-emerald-100 rounded p-2">
                        <span>Batch: <b>{b.batch_no}</b></span>
                        <span>Supplier: <b>{b.supplier_name || '-'}</b></span>
                        <span>Factory: <b>{b.factory || '-'}</b></span>
                        <span>Kg: <b>{(b.kg_received || 0).toLocaleString()}</b></span>
                        {b.linked_production_count > 0 && <span className="text-amber-700">⚠ already linked to {b.linked_production_count} production(s)</span>}
                      </div>
                    );
                  })()}
                </div>

                <div className="grid grid-cols-3 gap-4">
                  <div className="space-y-2">
                    <Label>Pack Size</Label>
                    <Select value={formData.pack_size} onValueChange={(v) => setFormData({ ...formData, pack_size: v })}>
                      <SelectTrigger data-testid="production-packsize-select"><SelectValue placeholder="Select" /></SelectTrigger>
                      <SelectContent>
                        {packSizeOptions.length === 0 && <SelectItem value="__none__" disabled>No pack sizes configured</SelectItem>}
                        {packSizeOptions.map(s => <SelectItem key={s} value={s}>{s}</SelectItem>)}
                      </SelectContent>
                    </Select>
                    <p className="text-[10px] text-slate-400">Manage in Settings → Pack Sizes</p>
                  </div>
                  <div className="space-y-2">
                    <Label>Actual Weight per Packet (kg)</Label>
                    <Input type="number" step="0.001" value={formData.actual_weight_per_packet} onChange={e => setFormData({ ...formData, actual_weight_per_packet: e.target.value })} placeholder="e.g., 1.95" data-testid="production-weight-input" />
                  </div>
                  <div className="space-y-2">
                    <Label>Packets per Bale</Label>
                    <Input type="number" step="0.01" value={formData.packets_per_bale} onChange={e => setFormData({ ...formData, packets_per_bale: e.target.value })} placeholder="e.g., 12" data-testid="production-ppb-input" />
                  </div>
                </div>

                <div className="grid grid-cols-3 gap-4">
                  <div className="space-y-2">
                    <Label>Number of Bales</Label>
                    <Input type="number" step="0.01" value={formData.num_bales} onChange={e => setFormData({ ...formData, num_bales: e.target.value })} placeholder="0" data-testid="production-bales-input" />
                  </div>
                  <div className="space-y-2">
                    <Label>Packaging Cost per Bale (KES)</Label>
                    <Input type="number" step="0.01" value={formData.packaging_cost_per_bale} onChange={e => setFormData({ ...formData, packaging_cost_per_bale: e.target.value })} placeholder="0" data-testid="production-cost-input" />
                  </div>
                  <div className="space-y-2">
                    <Label>Packaging Team</Label>
                    <Select value={formData.packaging_team_id} onValueChange={(v) => setFormData({ ...formData, packaging_team_id: v })}>
                      <SelectTrigger data-testid="production-team-select"><SelectValue placeholder="Optional" /></SelectTrigger>
                      <SelectContent>
                        {teams.filter(t => t.is_active).map(t => (
                          <SelectItem key={t.id} value={t.id}>{t.name}{t.leader ? ` — ${t.leader}` : ''}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                </div>

                {/* Auto-calculated preview */}
                <div className="bg-emerald-50 border border-emerald-100 rounded-lg p-3 grid grid-cols-3 gap-3 text-sm">
                  <div>
                    <div className="text-xs text-emerald-700">Total Packets</div>
                    <div className="font-bold text-emerald-900" data-testid="calc-total-packets">{fmt(formCalc.total_packets)}</div>
                  </div>
                  <div>
                    <div className="text-xs text-emerald-700">Total Weight Packed</div>
                    <div className="font-bold text-emerald-900" data-testid="calc-total-weight">{fmt(formCalc.total_weight_packed)} kg</div>
                  </div>
                  <div>
                    <div className="text-xs text-emerald-700">Total Amount Earned</div>
                    <div className="font-bold text-emerald-900" data-testid="calc-total-earned">{fmtKES(formCalc.total_amount_earned)}</div>
                  </div>
                </div>

                <div className="space-y-2">
                  <Label>Remarks</Label>
                  <Textarea value={formData.remarks} onChange={(e) => setFormData({ ...formData, remarks: e.target.value })} placeholder="Any notes about this production run" rows={2} data-testid="production-remarks-input" />
                </div>

                {/* Byproducts */}
                <div className="space-y-3 pt-3 border-t">
                  <div className="flex items-center justify-between">
                    <div>
                      <Label className="text-base font-semibold">Byproducts (Optional)</Label>
                      <p className="text-xs text-slate-500">Bran, Germ, Unga Baridi, Others</p>
                    </div>
                    <Button type="button" variant="outline" size="sm" onClick={addByproduct}><Plus className="h-4 w-4 mr-1" /> Add</Button>
                  </div>
                  {byproducts.length === 0 ? (
                    <div className="text-center py-3 text-slate-500 bg-slate-50 rounded-lg text-xs">
                      <Package className="h-5 w-5 mx-auto mb-1 text-slate-300" />
                      No byproducts recorded
                    </div>
                  ) : (
                    <div className="space-y-2">
                      {byproducts.map((bp) => (
                        <div key={bp.id} className="p-3 bg-amber-50 rounded-lg space-y-2">
                          <div className="grid grid-cols-2 gap-2">
                            <div>
                              <Label className="text-xs">Type *</Label>
                              <Select value={bp.name} onValueChange={(v) => updateByproduct(bp.id, 'name', v)}>
                                <SelectTrigger><SelectValue placeholder="Select" /></SelectTrigger>
                                <SelectContent>
                                  {STANDARD_BYPRODUCTS.map(n => <SelectItem key={n} value={n}>{n}</SelectItem>)}
                                </SelectContent>
                              </Select>
                            </div>
                            <div>
                              <Label className="text-xs">Quantity (kg) *</Label>
                              <Input type="number" value={bp.quantity_kg || ''} onChange={e => updateByproduct(bp.id, 'quantity_kg', e.target.value)} data-testid="byproduct-qty" />
                            </div>
                          </div>
                          <div className="grid grid-cols-2 gap-2">
                            <div>
                              <Label className="text-xs">Unit Price / kg</Label>
                              <Input type="number" step="0.01" value={bp.unit_price_per_kg ?? ''} onChange={e => updateByproduct(bp.id, 'unit_price_per_kg', e.target.value)} placeholder="0" data-testid="byproduct-unit-price" />
                            </div>
                            <div>
                              <Label className="text-xs">Total Value (editable)</Label>
                              <Input type="number" step="0.01" value={bp.total_value ?? ''} onChange={e => updateByproduct(bp.id, 'total_value', e.target.value)} placeholder="0" data-testid="byproduct-total-value" />
                            </div>
                          </div>
                          {bp.name === 'Others' && (
                            <div>
                              <Label className="text-xs">Description *</Label>
                              <Input value={bp.notes || ''} onChange={e => updateByproduct(bp.id, 'notes', e.target.value)} className="border-amber-300" />
                            </div>
                          )}
                          <div className="flex justify-end">
                            <Button type="button" variant="ghost" size="sm" onClick={() => removeByproduct(bp.id)} className="text-red-500"><Trash2 className="h-4 w-4" /></Button>
                          </div>
                        </div>
                      ))}
                    </div>
                  )}
                </div>

                <Button type="submit" className="w-full btn-primary" data-testid="save-production-btn">
                  {editingId ? 'Update Production' : 'Save Production'}
                </Button>
              </form>
            </DialogContent>
          </Dialog>
        </div>
      </div>

      {/* Filters */}
      <Card>
        <CardContent className="pt-4">
          <div className="flex flex-wrap items-center gap-3">
            <div className="flex items-center gap-1.5"><Filter className="h-4 w-4 text-slate-500" /></div>
            <div className="flex items-center gap-2">
              <Label className="text-xs">From:</Label>
              <DatePickerField date={filterStart} onDateChange={setFilterStart} buttonClassName="w-36 text-sm" displayFormat="MMM d, yyyy" placeholder="Start" />
            </div>
            <div className="flex items-center gap-2">
              <Label className="text-xs">To:</Label>
              <DatePickerField date={filterEnd} onDateChange={setFilterEnd} buttonClassName="w-36 text-sm" displayFormat="MMM d, yyyy" placeholder="End" />
            </div>
            <div className="flex items-center gap-2">
              <Label className="text-xs">Factory:</Label>
              <Select value={filterFactory} onValueChange={setFilterFactory}>
                <SelectTrigger className="w-36 text-sm" data-testid="filter-factory"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All</SelectItem>
                  <SelectItem value="Ndumberi">Ndumberi</SelectItem>
                  <SelectItem value="Githunguri">Githunguri</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div className="flex items-center gap-2">
              <Label className="text-xs">Pack:</Label>
              <Select value={filterPackSize} onValueChange={setFilterPackSize}>
                <SelectTrigger className="w-28 text-sm" data-testid="filter-packsize"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All</SelectItem>
                  {packSizeOptions.map(s => <SelectItem key={s} value={s}>{s}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div className="flex items-center gap-2">
              <Label className="text-xs">Team:</Label>
              <Select value={filterTeamId} onValueChange={setFilterTeamId}>
                <SelectTrigger className="w-44 text-sm" data-testid="filter-team"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All Teams</SelectItem>
                  {teams.map(t => <SelectItem key={t.id} value={t.id}>{t.name}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            {(filterStart || filterEnd || filterFactory !== 'all' || filterPackSize !== 'all' || filterTeamId !== 'all') && (
              <Button variant="ghost" size="sm" onClick={clearFilters}>Clear</Button>
            )}
            <div className="ml-auto text-xs text-slate-500">
              Showing {filtered.length} of {productions.length}
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Summary cards */}
      <div className="grid grid-cols-2 md:grid-cols-5 gap-4">
        <Card className="bg-emerald-50">
          <CardContent className="pt-6">
            <p className="text-xs uppercase tracking-wider text-emerald-700">Records</p>
            <p className="text-2xl font-bold text-emerald-800" data-testid="summary-records">{summary.records}</p>
          </CardContent>
        </Card>
        <Card className="bg-blue-50">
          <CardContent className="pt-6">
            <p className="text-xs uppercase tracking-wider text-blue-700">Bales</p>
            <p className="text-2xl font-bold text-blue-800" data-testid="summary-bales">{fmt(summary.bales)}</p>
          </CardContent>
        </Card>
        <Card className="bg-indigo-50">
          <CardContent className="pt-6">
            <p className="text-xs uppercase tracking-wider text-indigo-700">Packets</p>
            <p className="text-2xl font-bold text-indigo-800" data-testid="summary-packets">{fmt(summary.packets)}</p>
          </CardContent>
        </Card>
        <Card className="bg-purple-50">
          <CardContent className="pt-6">
            <p className="text-xs uppercase tracking-wider text-purple-700">Weight Packed</p>
            <p className="text-2xl font-bold text-purple-800" data-testid="summary-weight">{fmt(summary.weight)} kg</p>
          </CardContent>
        </Card>
        <Card className="bg-amber-50">
          <CardContent className="pt-6">
            <p className="text-xs uppercase tracking-wider text-amber-700">Earned</p>
            <p className="text-2xl font-bold text-amber-800" data-testid="summary-earned">{fmtKES(summary.earned)}</p>
          </CardContent>
        </Card>
      </div>

      {/* Records table */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2"><FileText className="h-5 w-5" /> Production Records</CardTitle>
        </CardHeader>
        <CardContent className="overflow-x-auto">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Date</TableHead>
                <TableHead>Factory</TableHead>
                <TableHead>Pack</TableHead>
                <TableHead className="text-right">Bales</TableHead>
                <TableHead className="text-right">Packets</TableHead>
                <TableHead className="text-right">Wt Packed</TableHead>
                <TableHead>Team</TableHead>
                <TableHead className="text-right">Earned</TableHead>
                <TableHead>Remarks</TableHead>
                <TableHead className="text-right">Actions</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {filtered.length === 0 ? (
                <TableRow><TableCell colSpan={10} className="text-center py-8 text-slate-500">No production records found</TableCell></TableRow>
              ) : (
                filtered.map(prod => (
                  <TableRow key={prod.id} data-testid={`production-row-${prod.id}`}>
                    <TableCell>{prod.date}</TableCell>
                    <TableCell>{prod.factory ? <Badge variant={prod.factory === 'Ndumberi' ? 'default' : 'secondary'}>{prod.factory}</Badge> : <span className="text-slate-400">-</span>}</TableCell>
                    <TableCell>{prod.pack_size || <span className="text-slate-400">-</span>}</TableCell>
                    <TableCell className="text-right">{prod.num_bales ? fmt(prod.num_bales) : '-'}</TableCell>
                    <TableCell className="text-right">{prod.total_packets ? fmt(prod.total_packets) : '-'}</TableCell>
                    <TableCell className="text-right">{prod.total_weight_packed ? `${fmt(prod.total_weight_packed)} kg` : '-'}</TableCell>
                    <TableCell className="text-sm">
                      {prod.packaging_team_id ? (
                        <Link to={`/packaging-teams/${prod.packaging_team_id}/statement`} className="text-emerald-700 hover:underline inline-flex items-center gap-1" data-testid={`team-link-${prod.id}`}>
                          {prod.packaging_team_name || 'Team'} <ExternalLink className="h-3 w-3" />
                        </Link>
                      ) : <span className="text-slate-400">-</span>}
                    </TableCell>
                    <TableCell className="text-right text-sm font-medium">{prod.total_amount_earned ? fmtKES(prod.total_amount_earned) : '-'}</TableCell>
                    <TableCell className="max-w-[220px]">
                      <p className="truncate text-sm" title={prod.remarks || prod.details}>{prod.remarks || prod.details || '-'}</p>
                    </TableCell>
                    <TableCell className="text-right">
                      <div className="flex justify-end gap-1">
                        <Link to={`/production/${prod.id}`}>
                          <Button variant="ghost" size="sm" title="View detail" data-testid={`view-production-${prod.id}`}>
                            <FileText className="h-4 w-4 text-emerald-700" />
                          </Button>
                        </Link>
                        <Button variant="ghost" size="sm" onClick={() => handleEdit(prod)} title="Edit" data-testid={`edit-production-${prod.id}`}>
                          <Edit className="h-4 w-4" />
                        </Button>
                        {isAdmin && (
                          <Button variant="ghost" size="sm" onClick={() => handleDelete(prod.id)} className="text-red-500" title="Delete" data-testid={`delete-production-${prod.id}`}>
                            <Trash2 className="h-4 w-4" />
                          </Button>
                        )}
                      </div>
                    </TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        </CardContent>
      </Card>

      {/* Teams Dialog */}
      <Dialog open={teamsDialogOpen} onOpenChange={(open) => { setTeamsDialogOpen(open); if (!open) resetTeamForm(); }}>
        <DialogContent className="sm:max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>Packaging Teams</DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <form onSubmit={handleTeamSubmit} className="bg-slate-50 rounded-lg p-3 space-y-3 border">
              <div className="font-medium text-sm">{editingTeamId ? 'Edit Team' : 'Add Team'}</div>
              <div className="grid grid-cols-2 gap-3">
                <div className="space-y-1">
                  <Label className="text-xs">Team Name *</Label>
                  <Input value={teamForm.name} onChange={e => setTeamForm({ ...teamForm, name: e.target.value })} placeholder="e.g., Team Alpha" data-testid="team-name-input" />
                </div>
                <div className="space-y-1">
                  <Label className="text-xs">Team Leader</Label>
                  <Input value={teamForm.leader} onChange={e => setTeamForm({ ...teamForm, leader: e.target.value })} placeholder="e.g., John Doe" />
                </div>
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div className="space-y-1">
                  <Label className="text-xs">Phone</Label>
                  <Input value={teamForm.phone} onChange={e => setTeamForm({ ...teamForm, phone: e.target.value })} placeholder="+254..." />
                </div>
                <div className="space-y-1 flex flex-col">
                  <Label className="text-xs">Status</Label>
                  <div className="flex items-center gap-2 h-9">
                    <Switch checked={teamForm.is_active} onCheckedChange={(v) => setTeamForm({ ...teamForm, is_active: v })} />
                    <span className="text-sm">{teamForm.is_active ? 'Active' : 'Inactive'}</span>
                  </div>
                </div>
              </div>
              <div className="space-y-1">
                <Label className="text-xs">Notes</Label>
                <Textarea value={teamForm.notes} onChange={e => setTeamForm({ ...teamForm, notes: e.target.value })} rows={2} />
              </div>
              <div className="flex gap-2 justify-end">
                {editingTeamId && <Button type="button" variant="outline" size="sm" onClick={resetTeamForm}>Cancel</Button>}
                <Button type="submit" size="sm" className="btn-primary" data-testid="save-team-btn">
                  {editingTeamId ? 'Update Team' : 'Add Team'}
                </Button>
              </div>
            </form>

            <div className="space-y-2">
              <div className="font-medium text-sm">All Teams ({teams.length})</div>
              {teams.length === 0 ? (
                <p className="text-sm text-slate-400 italic">No teams yet</p>
              ) : (
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>Name</TableHead>
                      <TableHead>Leader</TableHead>
                      <TableHead>Phone</TableHead>
                      <TableHead>Status</TableHead>
                      <TableHead className="text-right">Actions</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {teams.map(t => (
                      <TableRow key={t.id}>
                        <TableCell className="font-medium">{t.name}</TableCell>
                        <TableCell className="text-sm">{t.leader || '-'}</TableCell>
                        <TableCell className="text-sm">{t.phone || '-'}</TableCell>
                        <TableCell>{t.is_active ? <Badge>Active</Badge> : <Badge variant="secondary">Inactive</Badge>}</TableCell>
                        <TableCell className="text-right">
                          <div className="flex justify-end gap-1">
                            <Link to={`/packaging-teams/${t.id}/statement`} title="View Statement">
                              <Button variant="ghost" size="sm" className="text-emerald-700" data-testid={`view-statement-${t.id}`}>
                                <FileText className="h-4 w-4" />
                              </Button>
                            </Link>
                            <Button variant="ghost" size="sm" onClick={() => editTeam(t)}><Edit className="h-4 w-4" /></Button>
                            {isAdmin && (
                              <Button variant="ghost" size="sm" onClick={() => deleteTeam(t.id)} className="text-red-500"><Trash2 className="h-4 w-4" /></Button>
                            )}
                          </div>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              )}
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
